import { useEffect, useRef, useState } from "react";

export type PostalCodeLookupState =
  | "idle"
  | "searching"
  | "autofilled"
  | "found"
  | "not_found"
  | "error";

type ZipCloudAddress = {
  address1?: string;
  address2?: string;
  address3?: string;
};

type ZipCloudResponse = {
  status?: number;
  results?: ZipCloudAddress[] | null;
};

export const POSTAL_CODE_PATTERN = /^[0-9]{7}$/;

export function normalizePostalCodeInput(value: string): string {
  return value.replace(/\D/g, "").slice(0, 7);
}

export function usePostalCodeAddressAutofill({
  postalCode,
  addressLine1,
  enabled,
  setAddressLine1,
}: {
  postalCode: string;
  addressLine1: string;
  enabled: boolean;
  setAddressLine1: (value: string) => void;
}) {
  const [lookupState, setLookupState] = useState<PostalCodeLookupState>("idle");
  const addressLine1Ref = useRef(addressLine1);
  const lastAutofilledAddressRef = useRef<string | null>(null);

  useEffect(() => {
    addressLine1Ref.current = addressLine1;
  }, [addressLine1]);

  useEffect(() => {
    if (!enabled || postalCode.length !== 7) {
      setLookupState("idle");
      return;
    }

    const controller = new AbortController();
    setLookupState("searching");

    lookupPostalCodeAddress(postalCode, controller.signal)
      .then((address) => {
        if (!address) {
          setLookupState("not_found");
          return;
        }

        const currentAddress = addressLine1Ref.current.trim();
        const canAutofill =
          !currentAddress || currentAddress === lastAutofilledAddressRef.current;
        if (canAutofill) {
          setAddressLine1(address);
          lastAutofilledAddressRef.current = address;
          setLookupState("autofilled");
          return;
        }

        setLookupState("found");
      })
      .catch((error) => {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        setLookupState("error");
      });

    return () => controller.abort();
  }, [enabled, postalCode, setAddressLine1]);

  return lookupState;
}

async function lookupPostalCodeAddress(
  postalCode: string,
  signal: AbortSignal,
): Promise<string | null> {
  const response = await fetch(
    `https://zipcloud.ibsnet.co.jp/api/search?zipcode=${encodeURIComponent(
      postalCode,
    )}`,
    { signal },
  );
  if (!response.ok) {
    throw new Error("postal_code_lookup_failed");
  }

  const body = (await response.json()) as ZipCloudResponse;
  if (body.status !== 200 || !body.results?.length) {
    return null;
  }

  const first = body.results[0];
  const address = [first.address1, first.address2, first.address3]
    .map((part) => part?.trim() ?? "")
    .filter(Boolean)
    .join("");
  return address || null;
}
